import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Sparkles, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ClientAIChat() {
  const { user } = useAuth();
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("aurelis_ai_session");
    if (existing) return existing;
    const id = `s_${Math.random().toString(36).slice(2, 14)}`;
    localStorage.setItem("aurelis_ai_session", id);
    return id;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    fetch(`${API}/ai/chat/history?session_id=${encodeURIComponent(sessionId)}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", created_at: new Date().toISOString() }]);
    const text = input;
    setInput("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        acc += chunk;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Sorry — connection issue. Try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-6 lg:mt-0 mt-10" data-testid="client-ai-chat">
      <header>
        <div className="overline">AI Coach</div>
        <h1 className="font-serif text-5xl mt-3">Ask anything.</h1>
        <p className="text-secondary-luxe text-sm mt-3 max-w-xl">Programming, nutrition, recovery, mindset — Aurelis answers in seconds.</p>
      </header>
      <div className="card-luxe" style={{ height: "70vh" }}>
        <div ref={scrollRef} className="h-[calc(100%-72px)] overflow-y-auto pr-2 space-y-4">
          {messages.length === 0 && (
            <div className="text-secondary-luxe text-sm" data-testid="ai-empty">
              {`Try: "Design a 4-day strength program for a working mom" or "What should I eat post-workout?"`}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-5 py-3 text-sm ${
                m.role === "user" ? "bg-accent-luxe text-white" : "bg-surface-2 text-primary-luxe"
              }`} style={{ borderRadius: "2px" }}>
                {m.role === "assistant" && (
                  <div className="overline mb-2 flex items-center gap-2"><Sparkles size={11}/> Aurelis</div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{m.content || "…"}</div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="border-t border-luxe pt-3 mt-3 flex gap-2">
          <input className="input-luxe flex-1" placeholder="Ask Aurelis…" value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-input" />
          <button className="btn-primary" disabled={streaming || !input.trim()} data-testid="ai-send">
            <Send size={14} /> {streaming ? "…" : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
}
